import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import styles from './MyGallery.module.css'
import galleryIcon from '../../assets/icons/gallery.svg'
import Header from '../Layout/Header'
import { getAccessToken } from '../Auth/AuthStore'


const MyGallery = () => {
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('latest')

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 6 // 페이지당 항목 수

  const [allArtworks, setAllArtworks] = useState([])

  const [pageInfo, setPageInfo] = useState({
    pageNumber: 0,
    pageSize: itemsPerPage,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
    empty: true
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchArtworks = async (page) => {
    setLoading(true)
    setError(null)

    try {
      const token = getAccessToken()

      const apiSortBy = sortBy === 'latest' ? 'CREATED_AT' : 'NAME'

      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE}/api/drawing-analysis`,
      {
        params: {
          page: page - 1,
          size: itemsPerPage,
          sortBy: apiSortBy
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      const { drawingAnalysisList, pageInfo } = response.data.data

      setAllArtworks(drawingAnalysisList)
      setPageInfo(pageInfo)

      setCurrentPage(pageInfo.pageNumber + 1)
    } catch (err) {
      console.error('갤러리 조회 실패:', err)
      setError('갤러리를 불러오는 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchArtworks(currentPage)
  }, [currentPage, sortBy])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, sortBy])

  const handleCardClick = (drawingAnalysisId) => {
    navigate(`/drawing-analysis/${drawingAnalysisId}`)
  }

  // 삭제 핸들러
  const handleDelete = (artworkId) => {
    if (window.confirm('정말 이 작품을 삭제하시겠습니까?')) {
      setAllArtworks(prevArtworks => 
        prevArtworks.filter(artwork => artwork.id !== artworkId)
      )
    }
  }

  // 검색 로직
  const filteredArtworks = useMemo(() => {
    return allArtworks.filter((artwork) =>
      artwork.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [allArtworks, searchQuery])

  // 총 페이지 수 계산
  const totalPages = pageInfo.totalPages

return (
    <>
      <Header />
      <div className={styles.container}>
        <div className={styles.header}>
          <img src={galleryIcon} alt="gallery" />
          <h1>내 분석결과</h1>
        </div>

        {/* 검색 및 필터 */}
        <div className={styles.controls}>
          <div className={styles.searchBar}>
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="이미지 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className={styles.sortDropdown}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="latest">최신 순</option>
              <option value="name">이름 순</option>
            </select>
            <i className="fas fa-chevron-down"></i>
          </div>
        </div>

        {/* 로딩 / 에러 상태 */}
        {loading && (
          <div className={styles.loading}>
            갤러리를 불러오는 중입니다...
          </div>
        )}
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {/* 갤러리 그리드 */}
        {!loading && !error && (
          <div className={styles.gallery}>
            {pageInfo.empty || filteredArtworks.length === 0 ? (
              <div className={styles.emptyState}>
                <p>조회된 작품이 없습니다.</p>
              </div>
            ) : (
              filteredArtworks.map((artwork) => (
                <div
                  key={artwork.drawingAnalysisId}
                  className={styles.artworkCard}
                  onClick={() => handleCardClick(artwork.drawingAnalysisId)}
                >
                  <div className={styles.artworkImage}>
                    <div className={styles.placeholder}>
                      {/* 실제 그림 URL 사용 */}
                      <img
                        src={artwork.imageUrl}
                        alt={artwork.title}
                      />
                    </div>
                  </div>
                  <div className={styles.artworkInfo}>
                    <h3>{artwork.title}</h3>
                    <p>
                      {(artwork.drawnAt || artwork.createdAt || '').slice(0, 10)}
                    </p>
                  </div>
                  <div className={styles.artworkActions}>
                    {/*<button
                      className={styles.actionBtn}
                      title="삭제"
                      onClick={() => handleDelete(artwork.drawingAnalysisId)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>*/}
                    <button
                      className={styles.actionBtn}
                      title="다운로드"
                      onClick={(e) => {
                        e.stopPropagation() 
                        window.open(artwork.imageUrl, '_blank')
                      }}
                    >
                      <i className="fas fa-download"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* 페이지네이션 */}
        {!loading && totalPages > 0 && (
          <div className={styles.pagination}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`${styles.pageBtn} ${
                  currentPage === page ? styles.active : ''
                }`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default MyGallery
